import jwt_decode from 'jwt-decode';
import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import {
  AuthenticatePasswordlessUserByEmailDocument,
  AuthenticatePasswordlessUserByEmailInput,
  AuthenticatePasswordlessUserByEmailMutation,
  AuthenticatePasswordlessUserBySMSDocument,
  AuthenticatePasswordlessUserBySMSInput,
  AuthenticatePasswordlessUserBySMSMutation,
  client,
  getConfig,
  getGraphqlRequestClient,
} from '../graphql';
import { parseAustralianPhoneNumber } from '../utils';
import {
  AppMetadata,
  LocalStorageCache,
  clearAllResendCodeCounts,
  getFormattedAppMetadataCacheKey,
  getFormattedAuthCacheKey,
} from './cache';
import { ErrorTypes, TokenError, User } from './types';

const stub = () => {
  throw new Error('No provider was set');
};

interface Auth0ContextType {
  error: unknown;
  user: User | null;
  cookieKey?: string;
  isLoading: boolean;
  isVerifying: boolean;
  verificationCompleted: boolean;
  authorize: (parameters: AuthorizeParams, options?: any | undefined) => Promise<void>;
  getAccessTokenSilently: () => Promise<Credentials | TokenError | undefined>;
  setVerificationCompleted: (otp: string, key?: string) => void;
  getCurrentSession: () => AppMetadata | undefined;
  setCurrentSession: (key: string) => void;
  clearCredentials: () => void;
  clearAuthErrors: () => void;
}

// TO DO: fix type on GQL schema
export interface Credentials {
  accessToken?: string | null | undefined;
  idToken?: string | null | undefined;
  refreshToken?: string | null | undefined;
  scope?: string | null | undefined;
  expiresIn?: number | null | undefined;
}

const initialContext: Auth0ContextType = {
  error: null,
  user: null,
  isLoading: true,
  isVerifying: true,
  verificationCompleted: false,
  setVerificationCompleted: stub,
  authorize: stub,
  getAccessTokenSilently: stub,
  getCurrentSession: stub,
  setCurrentSession: stub,
  clearCredentials: stub,
  clearAuthErrors: stub,
};

type auth0Credentials = {
  access_token: string;
  id_token: string;
  refresh_token: string;
  expires_in: number;
};

export const Auth0Context = createContext<Auth0ContextType>(initialContext);

type PasswordlessUserByEmailInput = {
  type: 'email';
} & Omit<AuthenticatePasswordlessUserByEmailInput, 'scope' | 'audience'>;

type PasswordlessUserBySmsInput = {
  type: 'sms';
} & Omit<AuthenticatePasswordlessUserBySMSInput, 'scope' | 'audience'>;

export type AuthorizeParams = PasswordlessUserByEmailInput | PasswordlessUserBySmsInput;

const initialState = {
  user: null,
  error: null,
  isLoading: false,
  verificationCompleted: false,
  isVerifying: true,
};

function useAuth0() {
  const context = useContext(Auth0Context);

  if (!context) {
    throw new Error('useAuth0 must be used within a AuthProvider');
  }

  return context;
}

/**
 *  Reducer to maintain the state.
 */
type Action =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_COMPLETE'; user: User }
  | { type: 'LOGOUT_COMPLETE' }
  | { type: 'ERROR'; error: Error }
  | { type: 'RESET_ERROR' }
  | { type: 'INITIALIZED'; user: User | null }
  | { type: 'SET_USER'; user: User }
  | { type: 'SET_COOKIE_KEY'; cookieKey?: string }
  | { type: 'SET_VERIFICATION_COMPLETE' }
  | { type: 'SET_VERIFYING'; verifying: boolean };

export interface AuthState<TUser extends User = User> {
  error: Error | null;
  user: TUser | null;
  cookieKey?: string;
  isLoading: boolean;
  verificationCompleted: boolean;
  isVerifying: boolean;
}

const reducer = (state: AuthState, action: Action): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, isLoading: true, error: null };
    case 'LOGIN_COMPLETE':
      return { ...state, isLoading: false, error: null, user: action.user };

    case 'LOGOUT_COMPLETE':
      return { ...state, error: null, user: null, verificationCompleted: false };

    case 'ERROR':
      return { ...state, isLoading: false, error: action.error, verificationCompleted: false, isVerifying: false };

    case 'RESET_ERROR':
      return { ...state, error: null };

    case 'INITIALIZED':
      return { ...state, isLoading: false, user: action.user };

    case 'SET_USER':
      return { ...state, user: action.user };

    case 'SET_COOKIE_KEY':
      return { ...state, cookieKey: action.cookieKey };

    case 'SET_VERIFICATION_COMPLETE':
      return { ...state, verificationCompleted: true, isVerifying: false };

    case 'SET_VERIFYING':
      return { ...state, isVerifying: action.verifying };
  }
};

const DEFAULT_EXPIRY_ADJUSTMENT_SECONDS = 30;

const Auth0Provider = ({
  children,
  domain,
  clientId,
  userScope,
  datadogInstance,
}: {
  children: ReactNode;
  clientId: string;
  domain: string;
  userScope: string;
  datadogInstance?: typeof datadogRum;
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { url, audience } = getConfig();
  const requestClient = getGraphqlRequestClient(url);
  const cache = new LocalStorageCache();
  const cacheKey = parseAustralianPhoneNumber(state?.cookieKey || state?.user?.phone_number);
  const cacheKeyRef = useRef(cacheKey);

  useEffect(() => {
    cacheKeyRef.current = cacheKey;
  }, [cacheKey]);

  useEffect(() => {
    (async () => {
      let user: User | null = null;

      const cachedAuthData = cache.get(getFormattedAuthCacheKey(cacheKey));
      const credentials = cachedAuthData?.body as Credentials | undefined;
      const appMetadata = cache.get<AppMetadata>(getFormattedAppMetadataCacheKey(cacheKey));

      if (credentials?.idToken) {
        try {
          user = getIdTokenProfileClaims(credentials.idToken);
          if (appMetadata?.verificationCompleted) {
            dispatch({ type: 'SET_VERIFICATION_COMPLETE' });
          }
        } catch (error) {
          dispatch({ type: 'ERROR', error: error as Error });
        }
      } else if (appMetadata?.verificationCompleted && !credentials) {
        dispatch({ type: 'SET_VERIFYING', verifying: false });
      } else {
        dispatch({ type: 'SET_VERIFYING', verifying: false });
      }

      dispatch({ type: 'INITIALIZED', user });
    })();
  }, [cacheKey]);

  const getAccessTokenSilently = useCallback(async () => {
    const nowSeconds = Math.floor(Date.now() / 1000);
    const cacheAuthData = cache.get(getFormattedAuthCacheKey(cacheKey));

    const cacheTokenAboutToExpire =
      cacheAuthData && cacheAuthData.expiresAt - DEFAULT_EXPIRY_ADJUSTMENT_SECONDS < nowSeconds;

    if (cacheAuthData && cacheTokenAboutToExpire) {
      try {
        const credentials = await client<auth0Credentials>(`https://${domain}/oauth/token`, {
          data: {
            audience,
            scope: userScope,
            grant_type: 'refresh_token',
            refresh_token: cacheAuthData.body.refreshToken,
            client_id: clientId,
          },
        });

        if (credentials.id_token) {
          const user = getIdTokenProfileClaims(credentials.id_token);

          dispatch({ type: 'SET_USER', user });
        }

        const expiresAt = Math.floor(Date.now() / 1000) + credentials.expires_in;

        const credentialsParsed = {
          accessToken: credentials.access_token,
          idToken: credentials.id_token,
          refreshToken: credentials.refresh_token,
          expiresIn: credentials.expires_in,
        };

        cache.set(getFormattedAuthCacheKey(cacheKey), { expiresAt, body: credentialsParsed });

        return credentialsParsed;
      } catch (error) {
        datadogInstance?.addError(
          `Error with refresh token ${!!cacheAuthData.body.refreshToken ? 'present' : 'not present'}`,
          error as Error,
        );
        // Clear only this user's cache. Don't dispatch — any state change recreates
        // getAccessTokenSilently (depends on [state]), causing re-renders that abort in-flight fetches.
        // The TOKEN_ERROR return flows through useFetcher → React Query → ErrorBoundary.
        cache.removeAll(cacheKey);
        return {
          status: ErrorTypes.TOKEN_ERROR,
          message: 'Error fetching access token',
          error,
        };
      }
    }

    return cache.get(getFormattedAuthCacheKey(cacheKey))?.body;
  }, [state]);

  const authorize = useCallback(
    async (parameters: AuthorizeParams) => {
      dispatch({ type: 'LOGIN_START' });

      try {
        const { type, ...restInput } = parameters;

        let credentials: Credentials | undefined;
        let mobile: string | undefined;

        if (type === 'sms') {
          const { mobile: mobileNumber, ...smsInput } = restInput as Omit<
            AuthenticatePasswordlessUserBySMSInput,
            'scope' | 'audience'
          >;
          mobile = mobileNumber;

          ({ authenticatePasswordlessUserBySMS: credentials } =
            await requestClient.request<AuthenticatePasswordlessUserBySMSMutation>(
              AuthenticatePasswordlessUserBySMSDocument,
              {
                input: { ...smsInput, mobile: mobileNumber, scope: userScope, audience },
              },
            ));
        }

        if (type === 'email') {
          ({ authenticatePasswordlessUserByEmail: credentials } =
            await requestClient.request<AuthenticatePasswordlessUserByEmailMutation>(
              AuthenticatePasswordlessUserByEmailDocument,
              {
                input: { ...restInput, scope: userScope, audience },
              },
            ));
        }

        if (credentials?.idToken && credentials?.expiresIn) {
          const user = getIdTokenProfileClaims(credentials.idToken || '');

          dispatch({ type: 'LOGIN_COMPLETE', user });

          const expiresAt = Math.floor(Date.now() / 1000) + credentials.expiresIn;

          const key = cacheKeyRef.current || parseAustralianPhoneNumber(user?.phone_number || mobile);

          dispatch({ type: 'SET_COOKIE_KEY', cookieKey: key });

          cache.set(getFormattedAuthCacheKey(key), {
            expiresAt,
            body: credentials,
          });

          clearAllResendCodeCounts();
        }
      } catch (error) {
        dispatch({ type: 'ERROR', error: error as Error });
      }
    },
    [state],
  );

  const setVerificationCompleted = useCallback(
    (otp: string, key?: string) => {
      const cacheKey = parseAustralianPhoneNumber(key);
      const { otp: cachedOtp } = cache.get<AppMetadata>(getFormattedAppMetadataCacheKey(cacheKey)) || { otp: '' };

      cache.set<AppMetadata>(getFormattedAppMetadataCacheKey(cacheKey), {
        verificationCompleted: true,
        otp: otp ? otp : cachedOtp,
      });

      clearAllResendCodeCounts();

      dispatch({ type: 'SET_VERIFICATION_COMPLETE' });
    },
    [cache],
  );

  const setCurrentSession = useCallback((key: string) => {
    dispatch({ type: 'SET_COOKIE_KEY', cookieKey: key });
  }, []);

  const getCurrentSession = useCallback(() => {
    const credentials = cache.get<AppMetadata>(getFormattedAppMetadataCacheKey(cacheKey));
    return credentials;
  }, [cacheKey]);

  const clearCredentials = useCallback(() => {
    dispatch({ type: 'LOGOUT_COMPLETE' });
    cache.removeAll(cacheKey);
  }, [cache, cacheKey]);

  const clearAuthErrors = useCallback(() => {
    dispatch({ type: 'RESET_ERROR' });
  }, []);

  const contextValue = useMemo(
    () => ({
      ...state,
      setVerificationCompleted,
      authorize,
      getAccessTokenSilently,
      getCurrentSession,
      setCurrentSession,
      clearCredentials,
      clearAuthErrors,
    }),
    [
      state,
      setVerificationCompleted,
      authorize,
      getAccessTokenSilently,
      getCurrentSession,
      setCurrentSession,
      clearCredentials,
      clearAuthErrors,
    ],
  );

  return <Auth0Context.Provider value={contextValue}>{children}</Auth0Context.Provider>;
};

export { Auth0Provider, useAuth0 };

const idTokenNonProfileClaims = new Set([
  'iss',
  'aud',
  'exp',
  'nbf',
  'iat',
  'jti',
  'azp',
  'nonce',
  'auth_time',
  'at_hash',
  'c_hash',
  'acr',
  'amr',
  'sub_jwk',
  'cnf',
  'sip_from_tag',
  'sip_date',
  'sip_callid',
  'sip_cseq_num',
  'sip_via_branch',
  'orig',
  'dest',
  'mky',
  'events',
  'toe',
  'txn',
  'rph',
  'sid',
  'vot',
  'vtm',
]);

const getIdTokenProfileClaims = (idToken: string) => {
  const payload = jwt_decode(idToken) as User;

  const profileClaims = (Object.keys(payload) as Array<keyof User>).reduce((profile, claim) => {
    if (!idTokenNonProfileClaims.has(claim)) {
      profile[claim] = payload[claim];
    }

    return profile;
  }, {});

  return profileClaims as User;
};
